import React from "react";
import { Head, router, usePage } from "@inertiajs/react";
import styled from "styled-components";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalEmpty,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { apiPost } from "../../api";
import { SectionLoader } from "../../commons/Spinner";

// ---------------------------------------------------------------------------
// Expandable detail section — fetches paginated data from report_users API
// ---------------------------------------------------------------------------
function DetailSection({ title, endpoint, userId, columns }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [meta, setMeta] = React.useState(null);
  const perPage = 10;

  const fetchData = React.useCallback(
    async (pg) => {
      if (!endpoint || !userId) return;
      setLoading(true);
      try {
        const { parsedResponse, statusCode } = await apiPost(endpoint, {
          body: { id: userId, pagination: { page: pg, perpage: perPage } },
        });
        if (statusCode === 200 && parsedResponse) {
          const rows = parsedResponse.data || {};
          setData(Object.values(rows));
          setMeta(parsedResponse.meta || null);
          setPage(pg);
        }
      } catch (_) {
        /* silently fail */
      } finally {
        setLoading(false);
      }
    },
    [endpoint, userId]
  );

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data === null) fetchData(1);
  };

  const totalPages = meta?.pages || 1;

  return (
    <DetailWrap>
      <DetailHeader onClick={handleToggle}>
        <DetailTitle>{title}</DetailTitle>
        <DetailChevron $open={open}>&#9660;</DetailChevron>
      </DetailHeader>
      {open && (
        <DetailBody>
          {loading && <SectionLoader />}
          {!loading && data && data.length === 0 && (
            <PortalEmpty>Belum ada data.</PortalEmpty>
          )}
          {!loading && data && data.length > 0 && (
            <>
              <TableWrap>
                <DetailTable>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr key={row.index ?? idx}>
                        {columns.map((col) => (
                          <td key={col.key}>{col.render ? col.render(row) : row[col.key] ?? "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </DetailTable>
              </TableWrap>
              {totalPages > 1 && (
                <PaginationRow>
                  <PageBtn disabled={page <= 1} onClick={() => fetchData(page - 1)}>
                    &laquo; Prev
                  </PageBtn>
                  <PageInfo>
                    {page} / {totalPages}
                  </PageInfo>
                  <PageBtn disabled={page >= totalPages} onClick={() => fetchData(page + 1)}>
                    Next &raquo;
                  </PageBtn>
                </PaginationRow>
              )}
            </>
          )}
        </DetailBody>
      )}
    </DetailWrap>
  );
}

// ---------------------------------------------------------------------------
// User profile card for self scope
// ---------------------------------------------------------------------------
function UserProfileCard({ user, row }) {
  if (!user) return null;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <ProfileCard>
      <ProfileAvatar $hasImage={!!user.avatar_url} $src={user.avatar_url}>
        {!user.avatar_url && <ProfileInitial>{initial}</ProfileInitial>}
      </ProfileAvatar>
      <ProfileInfo>
        <ProfileName>{user.name}</ProfileName>
        <ProfileEmail>{user.email}</ProfileEmail>
        <ProfileMeta>
          {user.position && <MetaItem>{user.position}</MetaItem>}
          {user.company_name && <MetaItem>{user.company_name}</MetaItem>}
          {user.phone && <MetaItem>{user.phone}</MetaItem>}
        </ProfileMeta>
      </ProfileInfo>
      <ProfileStats>
        <StatBadge>
          <StatNum>{row?.total_lessons_watched ?? 0}</StatNum>
          <StatLabel>Materi</StatLabel>
        </StatBadge>
        <StatBadge>
          <StatNum>{row?.finished_exams ?? 0}{row?.total_exams ? ` / ${row.total_exams}` : ""}</StatNum>
          <StatLabel>Ujian Lulus</StatLabel>
        </StatBadge>
        <StatBadge>
          <StatNum>{row?.certificates_approved ?? 0}</StatNum>
          <StatLabel>Sertifikat</StatLabel>
        </StatBadge>
        {user.total_watch_time && (
          <StatBadge>
            <StatNum>{user.total_watch_time}</StatNum>
            <StatLabel>Durasi Belajar</StatLabel>
          </StatBadge>
        )}
      </ProfileStats>
    </ProfileCard>
  );
}

// ---------------------------------------------------------------------------
// Video columns
// ---------------------------------------------------------------------------
const VIDEO_COLS = [
  { key: "name", label: "Nama Materi" },
  { key: "progress", label: "Progress" },
  { key: "total_duration", label: "Durasi" },
  {
    key: "status",
    label: "Status",
    render: (r) => (
      <StatusChip $done={r.status === true || r.status === "Lulus"}>
        {r.status === true || r.status === "Lulus" ? "Selesai" : "Belum Selesai"}
      </StatusChip>
    ),
  },
];

const EXAM_COLS = [
  { key: "name", label: "Kurikulum" },
  { key: "exam_time", label: "Waktu" },
  { key: "exam_minimum_score", label: "KKM" },
  { key: "exam_score", label: "Skor" },
  {
    key: "status",
    label: "Status",
    render: (r) => (
      <StatusChip $done={r.status === true || r.status === "Lulus"}>
        {r.status === true || r.status === "Lulus" ? "Lulus" : "Belum Lulus"}
      </StatusChip>
    ),
  },
];

const WEBINAR_COLS = [
  { key: "title", label: "Judul" },
  { key: "speaker", label: "Pembicara" },
  { key: "date", label: "Tanggal" },
  {
    key: "status",
    label: "Status",
    render: (r) => (
      <StatusChip $done={r.status === true}>
        {r.status ? "Hadir" : "Terdaftar"}
      </StatusChip>
    ),
  },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function Reports() {
  const {
    scope = "self",
    scopes = [{ key: "self", label: "Laporan Saya" }],
    rows = [],
    summary = {},
    user_detail,
    detail_endpoints,
  } = usePage().props;

  const [search, setSearch] = React.useState("");

  const handleScopeChange = (next) => {
    if (next === scope) return;
    setSearch("");
    router.get(
      "/admin_company/reports",
      { scope: next },
      { preserveScroll: true, preserveState: true }
    );
  };

  const selfRow = scope === "self" && rows.length > 0 ? rows[0] : null;

  const filteredRows = React.useMemo(() => {
    if (scope !== "company" || !search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q)
    );
  }, [rows, search, scope]);

  return (
    <>
      <Head>
        <title>Laporan — Taxsam Learning Center</title>
        <meta name="description" content="Laporan pembelajaran Anda dan tim perusahaan." />
      </Head>
      <PortalContainer>
        {/* --- Scope selector + summary --- */}
        <SectionErrorBoundary name="ReportsScope">
          <PortalSection
            title="Laporan Pembelajaran"
            subtitle="Ringkasan progres berdasarkan cakupan terpilih."
            action={
              scopes.length > 1 ? (
                <SegmentBar role="tablist">
                  {scopes.map((s) => (
                    <SegmentBtn
                      key={s.key}
                      role="tab"
                      aria-selected={scope === s.key}
                      $active={scope === s.key}
                      onClick={() => handleScopeChange(s.key)}
                    >
                      {s.label}
                    </SegmentBtn>
                  ))}
                </SegmentBar>
              ) : null
            }
          >
            {/* User profile card for self scope */}
            {scope === "self" && user_detail && (
              <UserProfileCard user={user_detail} row={selfRow} />
            )}

            <SummaryGrid>
              <SummaryCard>
                <SummaryLabel>Anggota Diperiksa</SummaryLabel>
                <SummaryValue>{summary.members ?? 1}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Materi Ditonton</SummaryLabel>
                <SummaryValue>{summary.total_videos ?? 0}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Rata-rata Penyelesaian</SummaryLabel>
                <SummaryValue>{summary.avg_completion ?? 0}%</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Ujian Selesai</SummaryLabel>
                <SummaryValue>{summary.total_exams ?? 0}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Sertifikat Disetujui</SummaryLabel>
                <SummaryValue>{summary.total_certs ?? 0}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>
          </PortalSection>
        </SectionErrorBoundary>

        {/* --- Detail sections (self scope) --- */}
        {scope === "self" && detail_endpoints && (
          <SectionErrorBoundary name="ReportsDetail">
            <PortalSection title="Detail Laporan">
              <DetailSection
                title="Laporan Video"
                endpoint={detail_endpoints.videos}
                userId={detail_endpoints.user_id}
                columns={VIDEO_COLS}
              />
              <DetailSection
                title="Laporan Ujian"
                endpoint={detail_endpoints.exams}
                userId={detail_endpoints.user_id}
                columns={EXAM_COLS}
              />
              <DetailSection
                title="Live Online Class / Webinar"
                endpoint={detail_endpoints.webinars}
                userId={detail_endpoints.user_id}
                columns={WEBINAR_COLS}
              />
            </PortalSection>
          </SectionErrorBoundary>
        )}

        {/* --- Table: self / company --- */}
        <SectionErrorBoundary name="ReportsTable">
          <PortalSection
            title={scope === "company" ? "Per Anggota Perusahaan" : "Ringkasan Tabel"}
            action={
              scope === "company" ? (
                <SearchInput
                  type="text"
                  placeholder="Cari nama atau email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              ) : null
            }
          >
            {filteredRows.length > 0 ? (
              <TableWrap>
                <ReportsTable>
                  <thead>
                    <tr>
                      <th>Nama</th>
                      {scope === "company" && <th>Email</th>}
                      <th>Penyelesaian Materi</th>
                      <th>Ujian</th>
                      <th>Skor Rata-rata</th>
                      <th>Sertifikat</th>
                      {scope === "company" && <th>Peran</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => {
                      const pct = r.completion_pct ?? 0;
                      return (
                        <tr key={`${r.email || ""}-${idx}`}>
                          <td>
                            {r.name}
                            {r.is_self && <YouChip>Anda</YouChip>}
                          </td>
                          {scope === "company" && <td>{r.email}</td>}
                          <td>
                            <CompletionCell>
                              <CompletionBar
                                role="progressbar"
                                aria-valuenow={pct}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <CompletionFill style={{ width: `${pct}%` }} />
                              </CompletionBar>
                              <CompletionMeta>
                                {r.total_lessons_watched ?? 0}
                                {r.total_lessons_available > 0 && (
                                  <> / {r.total_lessons_available}</>
                                )}
                                {" "}({pct}%)
                              </CompletionMeta>
                            </CompletionCell>
                          </td>
                          <td>
                            {r.finished_exams ?? 0}
                            {r.total_exams ? <DetailMeta> / {r.total_exams}</DetailMeta> : null}
                          </td>
                          <td>{r.avg_score != null ? r.avg_score : "—"}</td>
                          <td>
                            {r.certificates_approved ?? 0}
                            {r.total_certs ? <DetailMeta> / {r.total_certs}</DetailMeta> : null}
                          </td>
                          {scope === "company" && (
                            <td>
                              <RolePill $admin={r.is_admin}>
                                {r.is_admin ? "Admin" : "Anggota"}
                              </RolePill>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </ReportsTable>
              </TableWrap>
            ) : (
              <PortalEmpty>Belum ada data laporan.</PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>
      </PortalContainer>
    </>
  );
}

Reports.layout = (page) => <PortalApp>{page}</PortalApp>;

// ---- Styles ----------------------------------------------------------------

const SegmentBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);

  [data-theme="dark"] & { background: rgba(255, 255, 255, 0.04); }
`;

const SegmentBtn = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${({ $active }) => ($active ? brand_gradient : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#4b5563")};
  font-size: 12px;
  font-weight: 700;
  transition: filter 150ms ease;

  &:hover:not([disabled]) { color: ${brand_orange_deep}; }
  ${({ $active }) => $active && `&:hover { color: #fff; filter: brightness(0.97); }`}
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }

  [data-theme="dark"] & {
    color: ${({ $active }) => ($active ? "#fff" : "#cbd5e1")};
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
`;

const SummaryCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 3px solid transparent;
  border-image: ${brand_gradient} 1;
`;

const SummaryLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const SummaryValue = styled.span`
  font-size: 28px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${brand_orange_deep};
`;

// --- Profile card ---

const ProfileCard = styled(PortalCard)`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  margin-bottom: 18px;
  border-left: 4px solid ${brand_orange_deep};

  @media ${narrowScreenMediaQuery} {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding: 16px;
  }
`;

const ProfileAvatar = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ $src, $hasImage }) =>
    $hasImage
      ? `center/cover no-repeat url("${$src}")`
      : "linear-gradient(135deg, #fcd34d, #fb923c)"};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${card_shadow_md};

  @media ${narrowScreenMediaQuery} {
    width: 48px;
    height: 48px;
  }
`;

const ProfileInitial = styled.span`
  font-size: 24px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.95);
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileName = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const ProfileEmail = styled.div`
  font-size: 13px;
  color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

const ProfileMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const MetaItem = styled.span`
  font-size: 11px;
  color: #9ca3af;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  [data-theme="dark"] & { background: rgba(255, 255, 255, 0.06); color: #94a3b8; }
`;

const ProfileStats = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media ${narrowScreenMediaQuery} {
    width: 100%;
    justify-content: space-between;
  }
`;

const StatBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(216, 89, 7, 0.06);

  [data-theme="dark"] & { background: rgba(255, 255, 255, 0.04); }
`;

const StatNum = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: ${brand_orange_deep};
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  [data-theme="dark"] & { color: #9ca3af; }
`;

// --- Detail sections ---

const DetailWrap = styled.div`
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: ${card_shadow_md};

  & + & { margin-top: 12px; }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: none;
  }
`;

const DetailHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: rgba(216, 89, 7, 0.04); }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: -2px; }
`;

const DetailTitle = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const DetailChevron = styled.span`
  font-size: 10px;
  color: #9ca3af;
  transition: transform 200ms ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
`;

const DetailBody = styled.div`
  padding: 0 20px 16px;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 10px 14px;
    text-align: left;
    font-size: 12px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }

  th {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    background: rgba(15, 23, 42, 0.02);
  }

  td { color: #1f2937; }

  [data-theme="dark"] & {
    th { color: #9ca3af; background: rgba(255, 255, 255, 0.03); }
    td { color: #e5e7eb; border-bottom-color: rgba(255, 255, 255, 0.06); }
  }
`;

const StatusChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $done }) =>
    $done ? "rgba(16, 185, 129, 0.14)" : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $done }) => ($done ? "#059669" : "#6b7280")};

  [data-theme="dark"] & {
    background: ${({ $done }) =>
      $done ? "rgba(16, 185, 129, 0.18)" : "rgba(255, 255, 255, 0.06)"};
    color: ${({ $done }) => ($done ? "#34d399" : "#9ca3af")};
  }
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 12px;
`;

const PageBtn = styled.button`
  padding: 5px 14px;
  border-radius: 999px;
  border: 1px solid rgba(216, 89, 7, 0.28);
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:hover:not(:disabled) { background: rgba(216, 89, 7, 0.06); }
`;

const PageInfo = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
`;

// --- Table ---

const SearchInput = styled.input`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(216, 89, 7, 0.28);
  background: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  width: 220px;

  &::placeholder { color: #9ca3af; }
  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
    box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.10);
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.06);
    color: #f3f4f6;
    border-color: rgba(216, 89, 7, 0.35);
  }

  @media ${narrowScreenMediaQuery} { width: 160px; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 14px;
`;

const ReportsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${card_shadow_md};

  th, td {
    padding: 12px 18px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }

  th {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    background: rgba(15, 23, 42, 0.02);
  }

  td { color: #1f2937; }

  tbody tr { transition: background 120ms ease; }
  tbody tr:hover { background: rgba(216, 89, 7, 0.03); }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    box-shadow: none;
    th { color: #9ca3af; background: rgba(255, 255, 255, 0.04); }
    td { color: #e5e7eb; border-bottom-color: rgba(255, 255, 255, 0.06); }
    tbody tr:hover { background: rgba(255, 255, 255, 0.04); }
  }
`;

const CompletionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
`;

const CompletionBar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;

  [data-theme="dark"] & { background: rgba(255, 255, 255, 0.10); }
`;

const CompletionFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
  transition: width 320ms ease;
`;

const CompletionMeta = styled.span`
  font-size: 11px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & { color: #9ca3af; }
`;

const DetailMeta = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const YouChip = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(216, 89, 7, 0.14);
  color: ${brand_orange_deep};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const RolePill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${({ $admin }) =>
    $admin ? "rgba(216, 89, 7, 0.14)" : "rgba(15, 23, 42, 0.06)"};
  color: ${({ $admin }) => ($admin ? brand_orange_deep : "#4b5563")};
`;
